// reportPages/TitlePage.js
import { C, hexToRgb, windowLabel, formatOffset } from "./pdfTheme";
import queues_logo_green_full from "../../../../assets/queues_logo_green.png"

export const generateTitlePage = async (pdf, options) => {
  const {
    authCtx,
    currentLocation,
    overviewState,
    timeRange,
    gamesWithNoData,
    loadTeamLogo,
    loadLogoImage,
    gameCards = []
  } = options;

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = margin;

  // Add team logo if available
  let teamLogoAdded = false;
  if (authCtx && authCtx.league && authCtx.venue) {
    try {
      const teamLogoData = await loadTeamLogo(authCtx.league, authCtx.venue);
      if (teamLogoData) {
        const img = new Image();
        img.src = teamLogoData;
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
        const maxLogoWidth = 50;
        const maxLogoHeight = 25;
        const imgAspectRatio = img.width / img.height;
        let logoWidth = maxLogoWidth;
        let logoHeight = logoWidth / imgAspectRatio;
        if (logoHeight > maxLogoHeight) {
          logoHeight = maxLogoHeight;
          logoWidth = logoHeight * imgAspectRatio;
        }
        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(teamLogoData, 'PNG', logoX, 20, logoWidth, logoHeight);
        teamLogoAdded = true;
        yPos = 20 + logoHeight + 15;
      }
    } catch (error) {
      console.error('Failed to load team logo:', error);
      yPos = 40;
    }
  } else {
    yPos = 40;
  }

  // Add title
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(C.text[0], C.text[1], C.text[2]);
  pdf.text('Concession Performance Report', pageWidth / 2, teamLogoAdded ? yPos : 40, { align: 'center' });
  yPos = teamLogoAdded ? yPos + 18 : 65;

  // Add concession logo
  let concessionLogoAdded = false;
  if (currentLocation.logoMobileURL || currentLocation.logoPath) {
    try {
      const logoData = await loadLogoImage(currentLocation.logoPath, currentLocation.logoMobileURL);
      if (logoData) {
        const img = new Image();
        
        const loadPromise = new Promise((resolve) => {
          let timeoutId;
          
          img.onload = () => {
            clearTimeout(timeoutId);
            resolve(true);
          };
          img.onerror = (error) => {
            clearTimeout(timeoutId);
            console.error('Concession logo failed to load:', error);
            resolve(false);
          };
          
          timeoutId = setTimeout(() => {
            console.warn('Concession logo load timeout');
            resolve(false);
          }, 5000);
        });
        
        img.src = logoData;
        const loaded = await loadPromise;
        
        if (loaded && img.complete && img.naturalHeight !== 0) {
          const maxLogoWidth = 60;
          const maxLogoHeight = 30;
          const imgAspectRatio = img.width / img.height;
          let logoWidth = maxLogoWidth;
          let logoHeight = logoWidth / imgAspectRatio;
          if (logoHeight > maxLogoHeight) {
            logoHeight = maxLogoHeight;
            logoWidth = logoHeight * imgAspectRatio;
          }
          const logoX = (pageWidth - logoWidth) / 2;
          
          // Create a canvas to handle transparency and resize
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Use reasonable resolution (not full 4K)
          const scaleFactor = 4; // Adjust for quality vs file size
          canvas.width = logoWidth * scaleFactor * 2.83; // Convert mm to pixels (~72 DPI)
          canvas.height = logoHeight * scaleFactor * 2.83;
          
          // Fill with white background to replace transparency
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw the image on top
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to base64 JPEG (better PDF compatibility)
          const processedImage = canvas.toDataURL('image/jpeg', 0.92);
          
          //console.log('Original size:', logoData.length, 'Processed size:', processedImage.length);
          
          pdf.addImage(processedImage, 'JPEG', logoX, yPos, logoWidth, logoHeight);
          concessionLogoAdded = true;
          yPos = yPos + logoHeight + 12;
        }
      }
    } catch (error) {
      console.error('Failed to load concession logo:', error);
    }
  }

  // Location name
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(C.text[0], C.text[1], C.text[2]);
  pdf.text(currentLocation.name || 'Concession Location', pageWidth / 2, yPos, { align: 'center' });
  yPos += 18;

  // Report date
  pdf.setFontSize(12);
  pdf.setTextColor(C.muted[0], C.muted[1], C.muted[2]);
  const reportDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric'
  });
  pdf.text(`Report Generated: ${reportDate}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 22;

  // Time window
  const label = (text) => {
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(C.brand[0], C.brand[1], C.brand[2]);
    pdf.text(text.toUpperCase(), margin, yPos);
    yPos += 7;
  };
  label('Time window');
  pdf.setFontSize(13);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(C.text[0], C.text[1], C.text[2]);
  pdf.text(`${windowLabel(timeRange)}: ${formatOffset(timeRange.startOffset)} to ${formatOffset(timeRange.endOffset)} relative to each game's start`, margin, yPos);
  yPos += 14;

  // Selected games (color swatches match the charts)
  label('Selected games');
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  overviewState.gameTitlesList.forEach((title, index) => {
    const gameDate = overviewState.selectedDates[index];
    const formattedDate = new Date(`${gameDate}T12:00:00`).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    const card = gameCards.find((g) => g.date === gameDate);
    if (card) {
      const rgb = hexToRgb(card.color);
      pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
      pdf.roundedRect(margin, yPos - 3.2, 3.6, 3.6, 0.8, 0.8, 'F');
    }
    pdf.setTextColor(C.text[0], C.text[1], C.text[2]);
    const cleanTitle = title && !title.includes('undefined') ? `  ${title}` : '';
    pdf.text(`${formattedDate}${cleanTitle}`, margin + 6, yPos);
    yPos += 7.5;
  });

  // Warning for games with no data
  if (gamesWithNoData.length > 0) {
    yPos += 12;
    pdf.setFillColor(C.warnTint[0], C.warnTint[1], C.warnTint[2]);
    pdf.roundedRect(margin, yPos - 6, pageWidth - 2 * margin, 18 + (gamesWithNoData.length * 6), 2, 2, 'F');
    pdf.setFillColor(C.warn[0], C.warn[1], C.warn[2]);
    pdf.rect(margin, yPos - 6, 1.4, 18 + (gamesWithNoData.length * 6), 'F');
    pdf.setTextColor(C.text[0], C.text[1], C.text[2]);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Note', margin + 5, yPos);
    yPos += 7;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(12);
    pdf.text('These games have no data in the selected time window:', margin + 5, yPos);
    yPos += 6;
    gamesWithNoData.forEach(game => {
      pdf.text(`• ${game}`, margin + 7, yPos);
      yPos += 6;
    });
  }

  // Add Queues logo at bottom of page
  try {
    const img = new Image();
    img.src = queues_logo_green_full;
    await new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });
    
    const maxLogoWidth = 45;
    const imgAspectRatio = img.width / img.height;
    const logoWidth = maxLogoWidth;
    const logoHeight = logoWidth / imgAspectRatio;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = pageHeight - 35; // 35mm from bottom
    
    pdf.addImage(queues_logo_green_full, 'PNG', logoX, logoY, logoWidth, logoHeight);
    
    // Optional: Add "Powered by" text above logo
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text('Powered by', pageWidth / 2, logoY - 3, { align: 'center' });
  } catch (error) {
    console.error('Failed to load Queues logo:', error);
  }
};